// AllDoctorScreen.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import DoctorProfileCard from "../components/DoctorProfileCard";
import DoctorFilterComponent from "../components/DoctorFilterComponent";
import PageTitle from "../components/PageTitleCompoent";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";
import "../css/AllDoctorScreen.css";
import api from "../api"; // Assuming you have an api.js file for API calls
import PawAnimation from "../components/PawAnimation";

export default function AllDoctorScreen() {
  const location = useLocation();
  const [doctors, setDoctors] = useState(location.state?.doctors || []);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [doctorToDelete, setDoctorToDelete] = useState({ id: null, name: "" });

  const navigate = useNavigate();

  const handleBack = () => navigate(-1);

  const [filters, setFilters] = useState({
    name: false,
    designation: false,
    activeDoctors: false,
    inactiveDoctors: false,
  });
  const [inputValues, setInputValues] = useState({ name: "", designation: "" });

  // ✅ NEW: Listen for status changes from localStorage
  useEffect(() => {
    // Brief loader for UI consistency
    const t = setTimeout(() => setLoading(false), 400);
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('doctorStatus_')) {
        const doctorId = parseInt(e.key.replace('doctorStatus_', ''));
        const newStatus = e.newValue;
        
        setDoctors((prevDoctors) =>
          prevDoctors.map((doc) =>
            doc.id === doctorId
              ? { ...doc, status: newStatus }
              : doc
          )
        );
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearTimeout(t);
    };
  }, []);

  // ✅ NEW: Check for status updates when component gains focus
  useEffect(() => {
    const checkStatusUpdates = () => {
      const updatedDoctors = doctors.map((doc) => {
        const savedStatus = localStorage.getItem(`doctorStatus_${doc.id}`);
        if (savedStatus && savedStatus !== doc.status) {
          return { ...doc, status: savedStatus };
        }
        return doc;
      });
      
      setDoctors(updatedDoctors);
    };

    window.addEventListener('focus', checkStatusUpdates);
    
    return () => {
      window.removeEventListener('focus', checkStatusUpdates);
    };
  }, [doctors]);

  // ✅ Delete Doctor
  const handleOpenDeleteModal = (id, name) => {
    setDoctorToDelete({ id, name });
    setIsModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsModalOpen(false);
    setDoctorToDelete({ id: null, name: "" });
  };

  const handleConfirmDelete = async () => {
    if (doctorToDelete && doctorToDelete.id) {
      try {
        await api.delete(`admin/doctor-profiles/${doctorToDelete.id}/`);
        setDoctors((prev) =>
          prev.filter((doc) => doc.id !== doctorToDelete.id)
        );
        // ✅ Clean up localStorage
        localStorage.removeItem(`doctorStatus_${doctorToDelete.id}`);
      } catch (error) {
        console.error("Failed to delete doctor:", error);
        alert("Failed to delete doctor. Please try again.");
      }
    }
    handleCloseDeleteModal();
  };

  // ✅ Toggle status
  const handleStatusToggle = (id) => {
    setDoctors((prevDoctors) =>
      prevDoctors.map((doc) =>
        doc.id === id
          ? {
              ...doc,
              status: doc.status === "active" ? "inactive" : "active",
            }
          : doc
      )
    );
  };

  // ✅ Filter Logic
  const handleFilterChange = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));
  const handleInputChange = (key, value) =>
    setInputValues((prev) => ({ ...prev, [key]: value }));

  const getFilteredDoctors = () => {
    return doctors.filter((doctor) => {
      const status = doctor.status?.toLowerCase();

      const matchName =
        !filters.name ||
        doctor.name?.toLowerCase().includes(inputValues.name.toLowerCase());
      const matchDesignation =
        !filters.designation ||
        doctor.specialization
          ?.toLowerCase()
          .includes(inputValues.designation.toLowerCase());

      // ✅ Fix: Treat "suspended" as inactive
      const matchActive =
        (!filters.activeDoctors && !filters.inactiveDoctors) ||
        (filters.activeDoctors && status === "active") ||
        (filters.inactiveDoctors &&
          ["inactive", "suspended", "disabled"].includes(status));

      return matchName && matchDesignation && matchActive;
    });
  };

  const filteredDoctors = getFilteredDoctors();

  if (loading) {
    return (
      <div className="full-screen-loader">
        <PawAnimation />
      </div>
    );
  }

  return (
    <div className="all-doctor-container">
      <Sidebar />
      <div className="all-doctor-main">
        <Header />
        <div className="all-doctor-content">
          <div className="all-doctor-heading-row">
            <PageTitle title="All Doctors" onBack={handleBack} />
            <div className="all-doctor-filter">
              <div className="add-doctor">
                <button
                  onClick={() => navigate("/add-doctor")}
                  className="add-doctor-button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="#eba8bf"
                      d="M19 12.998h-6v6h-2v-6H5v-2h6v-6h2v6h6z"
                    ></path>
                  </svg>
                  <span className="add-doctor-text">Add Doctor</span>
                </button>
              </div>
              <DoctorFilterComponent
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                filters={filters}
                inputValues={inputValues}
                onFilterChange={handleFilterChange}
                onInputChange={handleInputChange}
              />
            </div>
          </div>

          <div className="doctor-grid">
            {filteredDoctors.map((doctor) => (
              <DoctorProfileCard
                key={doctor.id}
                avatar={doctor.profile_image}
                name={doctor.name}
                speciality={doctor.specialization}
                rating={doctor.rating}
                reviews={doctor.reviews_count}
                active={doctor.status === "active"}
                onStatusToggle={() => handleStatusToggle(doctor.id)}
                onDelete={() => handleOpenDeleteModal(doctor.id, doctor.name)}
                doctor={doctor}
              />
            ))}
          </div>
        </div>
      </div>

      <DeleteWithConfirmation
        isOpen={isModalOpen}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={`Delete ${doctorToDelete?.name}?`}
        message="Are you sure you want to remove this doctor? This action cannot be undone."
      />
    </div>
  );
}